// ─── BLE Thermal Printer — ESC/POS printing via Web Bluetooth ─────────────────
//
// UUID discovery order (from scanner — BlueTooth Printer / HOIN):
//   1. 000018f0 / 00002af1  ← generic serial, most common
//   2. e7810a71 / bef8d6c9  ← iPosPrinter profile
//   3. 49535343 / 49535343-8841 ← Microchip RN4020
//
// Additional profiles included so the auto-discovery works for other brands
// (Xprinter, RONGTA, iMin, Star, etc.) without any config from the user.
// ──────────────────────────────────────────────────────────────────────────────

const PRINTER_PROFILES = [
  // Generic serial / most Chinese OEM (HOIN, Xprinter, RONGTA, etc.)
  { service: '000018f0-0000-1000-8000-00805f9b34fb', char: '00002af1-0000-1000-8000-00805f9b34fb' },
  // iPosPrinter
  { service: 'e7810a71-73ae-499d-8c15-faa9aef0c3f2', char: 'bef8d6c9-9c21-4c9e-b632-bd58c1009f9f' },
  // Microchip RN4020
  { service: '49535343-fe7d-4ae5-8fa9-9fafd205e455', char: '49535343-8841-43f4-a8d4-ecbe34729bb3' },
  // HC-05/HC-06 UART bridge
  { service: '0000ffe0-0000-1000-8000-00805f9b34fb', char: '0000ffe1-0000-1000-8000-00805f9b34fb' },
  // Nordic UART
  { service: '6e400001-b5a3-f393-e0a9-e50e24dcca9e', char: '6e400002-b5a3-f393-e0a9-e50e24dcca9e' },
  // AE series
  { service: '0000ae00-0000-1000-8000-00805f9b34fb', char: '0000ae01-0000-1000-8000-00805f9b34fb' },
];

const ALL_SERVICE_UUIDS = PRINTER_PROFILES.map(p => p.service);

// ─── ESC/POS byte constants ───────────────────────────────────────────────────
const ESC = 0x1b;
const GS  = 0x1d;

const CMD = {
  INIT:          new Uint8Array([ESC, 0x40]),
  ALIGN_LEFT:    new Uint8Array([ESC, 0x61, 0x00]),
  ALIGN_CENTER:  new Uint8Array([ESC, 0x61, 0x01]),
  ALIGN_RIGHT:   new Uint8Array([ESC, 0x61, 0x02]),
  BOLD_ON:       new Uint8Array([ESC, 0x45, 0x01]),
  BOLD_OFF:      new Uint8Array([ESC, 0x45, 0x00]),
  SIZE_NORMAL:   new Uint8Array([GS,  0x21, 0x00]),
  SIZE_2H:       new Uint8Array([GS,  0x21, 0x01]),  // double height
  LF:            new Uint8Array([0x0a]),
  FEED3:         new Uint8Array([ESC, 0x64, 0x04]),  // feed 4 lines before cut
  CUT:           new Uint8Array([GS,  0x56, 0x42, 0x00]),  // partial cut
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const enc = new TextEncoder();

function concat(...parts) {
  const arrays = parts.map(p => typeof p === 'string' ? enc.encode(p) : p);
  const len = arrays.reduce((s, a) => s + a.length, 0);
  const out = new Uint8Array(len);
  let off = 0;
  for (const a of arrays) { out.set(a, off); off += a.length; }
  return out;
}

const round2 = n => Math.round(((n || 0) + Number.EPSILON) * 100) / 100;

// ₹ (U+20B9) is a 3-byte UTF-8 sequence that most thermal printers render as
// three garbled code-page characters. Use ASCII "Rs" instead for reliable output.
function fmtMoney(n, curr = 'Rs') {
  return curr + round2(n).toFixed(2);
}


function leftRight(left, right, width) {
  const gap = width - left.length - right.length;
  if (gap <= 0) return (left.substring(0, width - right.length - 1) + ' ' + right).substring(0, width);
  return left + ' '.repeat(gap) + right;
}

function divider(width, ch = '-') {
  return ch.repeat(width);
}

// Word-wrap text into lines of at most maxWidth chars.
// Single words longer than maxWidth are hard-broken so columns never overflow.
function wrapText(text, maxWidth) {
  const lines = [];
  let line = '';
  for (let word of text.split(' ')) {
    // Hard-break any word that alone exceeds maxWidth
    while (word.length > maxWidth) {
      if (line) { lines.push(line); line = ''; }
      lines.push(word.substring(0, maxWidth));
      word = word.substring(maxWidth);
    }
    if (!word) continue;
    if (!line) {
      line = word;
    } else if (line.length + 1 + word.length <= maxWidth) {
      line += ' ' + word;
    } else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines.length ? lines : [''];
}

// ─── Receipt builder ─────────────────────────────────────────────────────────
export function buildEscPosReceipt({ order, mergedOrderNumbers, restaurant, billingSettings }) {
  const paperWidth = billingSettings?.thermal_paper_width || 58;
  const W = paperWidth === 58 ? 32 : 48;  // printable chars per line

  // Omit currency symbol — ₹ is not reliably printable on thermal code pages.
  const fmt = (n) => fmtMoney(n, '');

  const isGst   = !!(billingSettings?.gst_enabled && (order.gst_amount > 0 || order.applied_gst_percent > 0));
  const docTitle = isGst ? 'TAX INVOICE' : 'RECEIPT';
  const halfRate = round2((order.applied_gst_percent || 0) / 2);
  const cgst     = round2((order.gst_amount || 0) / 2);
  const sgst     = round2((order.gst_amount || 0) / 2);
  const gross    = round2((order.subtotal || 0) + (order.discount_amount || 0));
  const gstin    = billingSettings?.gstin || '';

  const dateObj = new Date(order.created_at);
  const dateStr = dateObj.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timeStr = dateObj.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  const parts = [];

  // ── Header ─────────────────────────────────────────────────────────────────
  // Use CMD.ALIGN_CENTER only — do NOT also call centerText() or the printer
  // will double-center (manual padding + hardware centering = right-shifted text)
  parts.push(CMD.INIT);
  parts.push(CMD.ALIGN_CENTER);
  parts.push(CMD.BOLD_ON, CMD.SIZE_2H);
  parts.push(concat(docTitle + '\n'));
  parts.push(CMD.SIZE_NORMAL);
  parts.push(concat((restaurant?.name || '') + '\n'));
  parts.push(CMD.BOLD_OFF);
  if (restaurant?.address)
    parts.push(concat(restaurant.address + '\n'));
  if (restaurant?.contact_phone)
    parts.push(concat(`Ph: ${restaurant.contact_phone}` + '\n'));
  if (isGst && gstin) {
    parts.push(CMD.BOLD_ON);
    parts.push(concat(`GSTIN: ${gstin}` + '\n'));
    parts.push(CMD.BOLD_OFF);
  }

  parts.push(CMD.ALIGN_LEFT);
  parts.push(concat(divider(W), '\n'));

  // ── Meta ───────────────────────────────────────────────────────────────────
  const ML = 14;  // label column width
  const addMeta = (label, value) => {
    const val = String(value);
    const maxVal = W - ML;
    if (val.length <= maxVal) {
      parts.push(concat(label.padEnd(ML) + val, '\n'));
    } else {
      // First line
      parts.push(concat(label.padEnd(ML) + val.substring(0, maxVal), '\n'));
      // Continuation lines indented by ML spaces
      let pos = maxVal;
      while (pos < val.length) {
        parts.push(concat(' '.repeat(ML) + val.substring(pos, pos + maxVal), '\n'));
        pos += maxVal;
      }
    }
  };

  addMeta('Invoice No', order.invoice_number || '-');
  if (order.order_number) {
    const allNums = mergedOrderNumbers?.length > 0
      ? [order.order_number, ...mergedOrderNumbers]
      : [order.order_number];
    const maxVal = W - ML;
    // Word-aware wrap: each order ID stays intact, never split mid-ID
    const lines = [];
    let currentLine = '';
    allNums.forEach((num, i) => {
      const token = num + (i < allNums.length - 1 ? ',' : '');
      if (!currentLine) {
        currentLine = token;
      } else if (currentLine.length + 1 + token.length <= maxVal) {
        currentLine += ' ' + token;
      } else {
        lines.push(currentLine);
        currentLine = token;
      }
    });
    if (currentLine) lines.push(currentLine);
    lines.forEach((line, i) => {
      parts.push(concat((i === 0 ? 'Order No' : '').padEnd(ML) + line, '\n'));
    });
  }
  addMeta('Date & Time', `${dateStr} ${timeStr}`);
  addMeta('Order Type', order.order_type === 'TAKEAWAY' ? 'Takeaway' : 'Dine-In');
  if (order.order_type !== 'TAKEAWAY' && order.restaurant_tables && !mergedOrderNumbers?.length) {
    const tbl = order.restaurant_tables.custom_name || `Table ${order.restaurant_tables.table_number}`;
    addMeta('Table', tbl);
  }

  parts.push(concat(divider(W), '\n'));

  // ── Items header ───────────────────────────────────────────────────────────
  // No currency prefix: max amount "9999.00" = 7 chars → CW_R/CW_A = 7.
  // Keeping QTY narrow (3) gives more room to item names.
  const CW_Q = W === 32 ? 3 : 4;   //  QTY  (up to 999)
  const CW_R = W === 32 ? 7 : 8;   //  RATE (up to 9999.00)
  const CW_A = W === 32 ? 7 : 8;   //  AMT
  const CW_N = W - CW_Q - CW_R - CW_A; // item name column (58mm → 15, 80mm → 28)

  const items = order.order_items || [];

  parts.push(CMD.BOLD_ON);
  parts.push(concat(
    'ITEM'.padEnd(CW_N) + 'QTY'.padStart(CW_Q) + 'RATE'.padStart(CW_R) + 'AMT'.padStart(CW_A),
    '\n'
  ));
  parts.push(CMD.BOLD_OFF);
  parts.push(concat(divider(W), '\n'));

  // ── Items ──────────────────────────────────────────────────────────────────
  // First name chunk + QTY/RATE/AMT always on the same first line (mirrors
  // tax invoice layout). Any remaining name words wrap to lines below.
  for (const oi of items) {
    const nameLines = wrapText(oi.menu_items?.name || 'Item', CW_N);
    const qty  = String(oi.quantity).padStart(CW_Q);
    const rate = fmt(oi.unit_price).padStart(CW_R);
    const amt  = fmt(oi.unit_price * oi.quantity).padStart(CW_A);

    parts.push(concat(nameLines[0].padEnd(CW_N) + qty + rate + amt, '\n'));
    for (let j = 1; j < nameLines.length; j++) {
      parts.push(concat(nameLines[j], '\n'));
    }
  }

  parts.push(concat(divider(W), '\n'));

  // ── Totals ─────────────────────────────────────────────────────────────────
  const addRow = (label, value, bold = false) => {
    if (bold) parts.push(CMD.BOLD_ON);
    parts.push(concat(leftRight(label, value, W), '\n'));
    if (bold) parts.push(CMD.BOLD_OFF);
  };

  addRow('Subtotal', fmt(gross));

  if ((order.discount_amount || 0) > 0)
    addRow('Discount', `-${fmt(order.discount_amount)}`);

  const showTaxable = (order.discount_amount || 0) > 0 || (order.gst_amount || 0) > 0 || (order.service_charge_amount || 0) > 0;
  if (showTaxable)
    addRow('Taxable Amt', fmt(order.subtotal));

  if (isGst) {
    addRow(`CGST @ ${halfRate}%`, fmt(cgst));
    addRow(`SGST @ ${halfRate}%`, fmt(sgst));
  }

  if ((order.service_charge_amount || 0) > 0)
    addRow(`Svc Chg @ ${order.applied_service_charge_percent || ''}%`, fmt(order.service_charge_amount));

  parts.push(concat(divider(W), '\n'));
  addRow('TOTAL PAYABLE', fmt(order.total_payable), true);
  parts.push(concat(divider(W), '\n'));

  // ── Payment ────────────────────────────────────────────────────────────────
  if (order.paid_at) {
    addRow(`Payment: ${(order.payment_method || '').toUpperCase()}`, 'PAID');
  } else {
    addRow('Payment pending', 'UNPAID');
  }
  parts.push(concat(divider(W), '\n'));

  // ── Footer ─────────────────────────────────────────────────────────────────
  parts.push(CMD.ALIGN_CENTER, CMD.BOLD_ON);
  parts.push(concat('Thank you for dining with us!\n'));
  parts.push(CMD.BOLD_OFF);
  parts.push(concat('System generated document\n'));
  parts.push(concat(docTitle + '\n'));

  // ── Feed + cut ─────────────────────────────────────────────────────────────
  parts.push(CMD.FEED3, CMD.CUT);

  return concat(...parts);
}

// ─── BLE connect + print ─────────────────────────────────────────────────────
async function writeChunked(char, data) {
  const CHUNK = 200;
  for (let i = 0; i < data.length; i += CHUNK) {
    const chunk = data.slice(i, i + CHUNK);
    try {
      await char.writeValueWithResponse(chunk);
    } catch (_) {
      await char.writeValue(chunk);
    }
  }
}

// ─── Last-used printer memory (localStorage) ─────────────────────────────────
const LS_KEY = 'menuron_ble_printer';

function savePrinterInfo(device) {
  try {
    const existing = (() => { try { return JSON.parse(localStorage.getItem(LS_KEY)); } catch (_) { return null; } })();
    // Preserve custom name if it's the same device
    const name = (existing && existing.id === device.id) ? existing.name : (device.name || 'Printer');
    localStorage.setItem(LS_KEY, JSON.stringify({ id: device.id, name }));
  } catch (_) {}
}

export function bleGetLastPrinterName() {
  try {
    const saved = localStorage.getItem(LS_KEY);
    return saved ? JSON.parse(saved).name : null;
  } catch (_) { return null; }
}

export function bleUpdateLastPrinterName(label) {
  try {
    const saved = localStorage.getItem(LS_KEY);
    if (!saved) return;
    const info = JSON.parse(saved);
    info.name = label;
    localStorage.setItem(LS_KEY, JSON.stringify(info));
  } catch (_) {}
}

export function bleClearLastPrinter() {
  if (_isNativeApp()) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'BLE_CLEAR_PRINTER' }));
    return;
  }
  try { localStorage.removeItem(LS_KEY); } catch (_) {}
}

// ─── GATT connect with timeout (Web Bluetooth has no built-in timeout) ────────
function gattConnect(device, timeoutMs = 8000) {
  return Promise.race([
    device.gatt.connect(),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Printer connection timed out. Make sure the printer is on and nearby.')), timeoutMs)
    ),
  ]);
}

// ─── Connect to a specific device and write ───────────────────────────────────
async function connectDeviceAndWrite(device, receiptData) {
  const server = await gattConnect(device);

  let printerChar = null;
  for (const profile of PRINTER_PROFILES) {
    try {
      const svc = await server.getPrimaryService(profile.service);
      printerChar = await svc.getCharacteristic(profile.char);
      break;
    } catch (_) {}
  }

  if (!printerChar) {
    server.device.gatt.disconnect();
    throw new Error(
      'Could not find a writable characteristic on this printer.\n' +
      'Run the BLE Scanner at /ble-scanner.html and share the output.'
    );
  }

  try {
    await writeChunked(printerChar, receiptData);
  } finally {
    await new Promise(r => setTimeout(r, 800));
    server.device.gatt.disconnect();
  }
}

// ─── React Native WebView bridge ─────────────────────────────────────────────
//
// When running inside the Menuron Android app (WebView), Bluetooth printing is
// handled natively. The web sends raw ESC/POS bytes as a hex string via
// postMessage; the native app prints and calls back via injected JS.
//
function _isNativeApp() {
  return typeof window !== 'undefined' && !!window.ReactNativeWebView;
}

// ─── Main print entry point ───────────────────────────────────────────────────
//
// Flow (Web Bluetooth):
//  1. Saved printer + getDevices() finds it → auto-connect, NO picker ever shown
//  2. Saved printer but getDevices() doesn't find it → picker (Chrome lost permission)
//  3. No saved printer → picker (first time)
//  4. Auto-connect times out → throw so caller can show "Printer offline, Retry"
//     (NEVER silently fall back to picker once a printer has been saved)
//
// Flow (Native WebView):
//  - Converts bytes to hex, sends postMessage to native, awaits callback.
//
export async function bleConnectAndPrint(receiptData) {
  // ── Native WebView: delegate to Android BLE ──────────────────────────────
  if (_isNativeApp()) {
    const hex = Array.from(receiptData).map(b => b.toString(16).padStart(2, '0')).join('');
    return new Promise((resolve, reject) => {
      window._menuronBlePrintResolve = resolve;
      window._menuronBlePrintReject = reject;
      const timer = setTimeout(() => {
        if (window._menuronBlePrintReject) {
          window._menuronBlePrintReject(new Error('Bluetooth print timed out'));
          window._menuronBlePrintResolve = null;
          window._menuronBlePrintReject = null;
        }
      }, 30000);
      window._menuronBleTimer = timer;
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'BLE_PRINT_RAW', hex }));
    });
  }

  if (!navigator.bluetooth) {
    throw new Error('Web Bluetooth is not supported. Use Chrome on Windows, Mac, or Android.');
  }

  const saved = (() => { try { return JSON.parse(localStorage.getItem(LS_KEY)); } catch (_) { return null; } })();

  // In Electron, tell the main process the saved printer name for auto-select
  if (window.menuronDesktop?.bluetooth && saved?.name) {
    await window.menuronDesktop.bluetooth.setAutoName(saved.name).catch(() => {})
  }

  // ── Case 1: we have a saved printer ─────────────────────────────────────────
  // In Electron, skip getDevices() — it returns stale browser permissions.
  // Always go through requestDevice() so the main-process handler can auto-select.
  if (saved && navigator.bluetooth.getDevices && !window.menuronDesktop?.isDesktop) {
    const granted = await navigator.bluetooth.getDevices().catch(() => []);
    const last = granted.find(d => d.id === saved.id);

    if (last) {
      // Auto-connect — if this throws (timeout, out of range) we let it bubble up.
      // Caller shows "Printer offline — Retry" instead of falling back to picker.
      await connectDeviceAndWrite(last, receiptData);
      savePrinterInfo(last);
      return;
    }
    // getDevices() didn't return the device (Chrome lost the permission grant).
    // Fall through to picker so the user can re-select once.
  }

  // ── Case 2: first time OR Chrome lost permission → show picker ───────────────
  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: ALL_SERVICE_UUIDS,
  });

  await connectDeviceAndWrite(device, receiptData);
  savePrinterInfo(device);
}

export async function blePrintReceipt({ order, mergedOrderNumbers, restaurant, billingSettings }) {
  const data = buildEscPosReceipt({ order, mergedOrderNumbers, restaurant, billingSettings });
  await bleConnectAndPrint(data);
}

// ─── KOT builder ─────────────────────────────────────────────────────────────
export function buildEscPosKOT({ order, restaurant, billingSettings }) {
  const paperWidth = billingSettings?.thermal_paper_width || 58;
  const W = paperWidth === 58 ? 32 : 48;

  const dateObj = new Date(order.created_at);
  const dateStr = dateObj.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timeStr = dateObj.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  const ML = 14;
  const addMeta = (parts, label, value) => {
    const val = String(value);
    const maxVal = W - ML;
    if (val.length <= maxVal) {
      parts.push(concat(label.padEnd(ML) + val, '\n'));
    } else {
      parts.push(concat(label.padEnd(ML) + val.substring(0, maxVal), '\n'));
      let pos = maxVal;
      while (pos < val.length) {
        parts.push(concat(' '.repeat(ML) + val.substring(pos, pos + maxVal), '\n'));
        pos += maxVal;
      }
    }
  };

  const parts = [];
  parts.push(CMD.INIT);
  parts.push(CMD.ALIGN_CENTER);
  parts.push(CMD.BOLD_ON, CMD.SIZE_2H);
  parts.push(concat('KOT\n'));
  parts.push(CMD.SIZE_NORMAL);
  parts.push(concat((restaurant?.name || '') + '\n'));
  parts.push(CMD.BOLD_OFF);
  parts.push(CMD.ALIGN_LEFT);
  parts.push(concat(divider(W), '\n'));

  addMeta(parts, 'Order / KOT No', `#${order.order_number || '-'}`);
  addMeta(parts, 'Date & Time', `${dateStr} ${timeStr}`);
  addMeta(parts, 'Type', order.order_type === 'TAKEAWAY' ? 'Takeaway' : 'Dine-In');
  if (order.order_type !== 'TAKEAWAY' && order.restaurant_tables) {
    const tbl = order.restaurant_tables.custom_name || `Table ${order.restaurant_tables.table_number}`;
    addMeta(parts, 'Table', tbl);
  }

  parts.push(concat(divider(W), '\n'));

  // Items: ITEM + QTY only (no prices)
  const NW = W - 6;
  const QW = 6;
  parts.push(CMD.BOLD_ON);
  parts.push(concat(('ITEM'.padEnd(NW) + 'QTY'.padStart(QW)).substring(0, W), '\n'));
  parts.push(CMD.BOLD_OFF);
  parts.push(concat(divider(W), '\n'));

  for (const oi of (order.order_items || [])) {
    const nameLines = wrapText(oi.menu_items?.name || 'Item', NW);
    const qty = String(oi.quantity).padStart(QW);
    parts.push(CMD.BOLD_ON);
    parts.push(concat(nameLines[0].padEnd(NW) + qty, '\n'));
    parts.push(CMD.BOLD_OFF);
    for (let j = 1; j < nameLines.length; j++) {
      parts.push(concat(nameLines[j], '\n'));
    }
  }

  parts.push(concat(divider(W), '\n'));

  if (order.notes) {
    parts.push(CMD.BOLD_ON);
    parts.push(concat('Notes:\n'));
    parts.push(CMD.BOLD_OFF);
    const notes = order.notes;
    for (let i = 0; i < notes.length; i += W) {
      parts.push(concat(notes.substring(i, i + W) + '\n'));
    }
    parts.push(concat(divider(W), '\n'));
  }

  parts.push(CMD.FEED3, CMD.CUT);
  return concat(...parts);
}

export async function blePrintKOT({ order, restaurant, billingSettings }) {
  const data = buildEscPosKOT({ order, restaurant, billingSettings });
  await bleConnectAndPrint(data);
}
